import type {
  AnalyticsActionOrigin,
  AnalyticsCurrentPageSectionState,
  AnalyticsCurrentPageSubsection,
  AnalyticsLinkEvent,
} from 'src/modules/analytics/types';
import type { Action, Card, Mode, Slug } from 'src/types';

import { createResumeRoutes } from './routes';
import type { CreateResumeRoute } from './types';

export enum CreateResumeActions {
  RESET_ROUTES = 'RESET_ROUTES',
  SET_ROUTE_AS_TOUCHED = 'SET_ROUTE_AS_TOUCHED',
  SET_ALL_ROUTES_AS_TOUCHED = 'SET_ALL_ROUTES_AS_TOUCHED',
  UPDATE_CREATE_STEP = 'UPDATE_CREATE_STEP',
  CREATE_RESUME_RENDERED = 'CREATE_RESUME_RENDERED',
  RETURN_TO_NON_PROFILE_PAGE = 'RETURN_TO_NON_PROFILE_PAGE',
}

export enum CreateStep {
  PROFILE_REVIEW = 'PROFILE_REVIEW',
  RESUME_LIMIT_REACHED = 'RESUME_LIMIT_REACHED',
  RESUME_CREATED = 'RESUME_CREATED',
}

export interface CreateResumeState {
  pages: Record<
    string,
    {
      touched: boolean;
    }
  >;
  createStep: CreateStep;
}

const initialState: CreateResumeState = {
  pages: {},
  createStep: CreateStep.PROFILE_REVIEW,
};

createResumeRoutes.forEach((route) => {
  initialState.pages[route] = {
    touched: false,
  };
});

export interface ProfilePageSettings {
  card?: Slug;
  field?: string;
  mode?: Mode;
  item?: string;
  previousPage?: string;
  returnUrl?: string;
  formData?: string;
  profileDataSource?: string;
  features?: string;
}

export interface UpdateRouteParams {
  card: Card;
  mode: Mode;
  params?: Record<string, any>;
  analytics?: {
    origin?: AnalyticsActionOrigin;
    actionName?: AnalyticsLinkEvent;
    currentPageSectionState?: AnalyticsCurrentPageSectionState;
    currentPageSubsection?: AnalyticsCurrentPageSubsection;
  };
}

export type CreateResumeAction =
  | (Action<CreateResumeActions.SET_ROUTE_AS_TOUCHED> & {
      payload: CreateResumeRoute;
    })
  | Action<CreateResumeActions.SET_ALL_ROUTES_AS_TOUCHED>
  | (Action<CreateResumeActions.UPDATE_CREATE_STEP> & {
      payload: { from: CreateStep; to: CreateStep };
    })
  | Action<CreateResumeActions.RESET_ROUTES>
  | Action<CreateResumeActions.CREATE_RESUME_RENDERED>
  | Action<CreateResumeActions.RETURN_TO_NON_PROFILE_PAGE>
  | Action<CreateResumeActions.RETURN_TO_NON_PROFILE_PAGE>;

export const createResumeReducer = (
  state = initialState,
  action: CreateResumeAction,
): CreateResumeState => {
  switch (action.type) {
    case CreateResumeActions.SET_ROUTE_AS_TOUCHED: {
      const to = action.payload;

      return {
        ...state,
        pages: {
          ...state.pages,
          [to]: {
            ...state.pages[to],
            touched: true,
          },
        },
      };
    }

    case CreateResumeActions.SET_ALL_ROUTES_AS_TOUCHED: {
      const newPageState = { ...state.pages };

      Object.keys(newPageState).map((page) => {
        newPageState[page].touched = true;
      });

      return {
        ...state,
        pages: {
          ...newPageState,
        },
      };
    }

    case CreateResumeActions.UPDATE_CREATE_STEP: {
      const { to: createStep } = action.payload;

      return {
        ...state,
        createStep,
      };
    }

    case CreateResumeActions.RESET_ROUTES: {
      return initialState;
    }

    default:
      return state;
  }
};
